import type { SettingsNavEntry, SettingsNavGroupDefinition } from '@common/types';

/**
 * The organisation settings catalogue. Groups follow the ownership split in the settings spec, and
 * they are the top-level tabs; the entries inside one are the pills under them. Keeping the two
 * levels in one catalogue is what stops the tab row and the pill row disagreeing.
 */
export const ORGANIZATION_SETTINGS_NAV: ReadonlyArray<SettingsNavGroupDefinition> = [
  {
    key: 'organisation',
    label: 'Organisation',
    icon: 'corporate_fare',
    entries: [
      { key: 'profile', label: 'Profile', path: 'profile' },
      { key: 'accounts', label: 'Accounts', path: 'accounts' },
    ],
  },
  {
    key: 'people',
    label: 'People & access',
    icon: 'group',
    entries: [
      { key: 'users', label: 'Users', path: 'users' },
      { key: 'roles', label: 'Roles', path: 'roles' },
      { key: 'access-policy', label: 'Access policy', path: 'access-policy' },
      { key: 'audit', label: 'Audit log', path: 'audit' },
    ],
  },
  {
    key: 'taxonomy',
    label: 'Tags & views',
    icon: 'sell',
    entries: [
      { key: 'tags', label: 'Tags', path: 'tags' },
      { key: 'views', label: 'Views', path: 'views' },
      { key: 'tag-categories', label: 'Categories', path: 'tag-categories' },
    ],
  },
  {
    key: 'standards',
    label: 'Standards',
    icon: 'rule',
    entries: [
      { key: 'templates', label: 'Questionnaire templates', path: 'templates' },
      { key: 'email-templates', label: 'Email & sender', path: 'email-templates' },
      { key: 'departments', label: 'Departments', path: 'departments' },
      { key: 'ticket-policy', label: 'Ticket policy', path: 'ticket-policy' },
      { key: 'milestones', label: 'Milestones', path: 'milestones', requiresFeature: 'milestones' },
    ],
  },
  {
    key: 'data',
    label: 'Data & integrations',
    icon: 'hub',
    entries: [
      { key: 'review-sources', label: 'Review sources', path: 'review-sources' },
      { key: 'data-import', label: 'Data import', path: 'data-import' },
      { key: 'api-keys', label: 'API & webhooks', path: 'api-keys' },
      { key: 'privacy', label: 'Data & privacy', path: 'privacy' },
    ],
  },
  {
    key: 'commercial',
    label: 'Billing',
    icon: 'credit_card',
    entries: [
      { key: 'billing', label: 'Invoices', path: 'billing' },
      { key: 'usage', label: 'Usage', path: 'usage' },
      { key: 'notifications', label: 'Notifications', path: 'notifications' },
    ],
  },
];

/** The account settings catalogue. An account owns the property and its guest experience. */
export const ACCOUNT_SETTINGS_NAV: ReadonlyArray<SettingsNavGroupDefinition> = [
  {
    key: 'property',
    label: 'Property',
    icon: 'apartment',
    entries: [
      { key: 'profile', label: 'Profile', path: 'profile' },
      { key: 'brand', label: 'Brand', path: 'brand' },
      { key: 'tags', label: 'Tags', path: 'tags' },
    ],
  },
  {
    key: 'people',
    label: 'People',
    icon: 'group',
    entries: [
      { key: 'users', label: 'Users', path: 'users' },
      { key: 'roles', label: 'Roles', path: 'roles' },
      { key: 'departments', label: 'Departments', path: 'departments' },
    ],
  },
  {
    key: 'guest-experience',
    label: 'Guest experience',
    icon: 'fact_check',
    entries: [
      { key: 'questionnaires', label: 'Questionnaires', path: 'questionnaires' },
      { key: 'delivery', label: 'Delivery', path: 'delivery' },
      { key: 'guest-data', label: 'Guest data', path: 'guest-data' },
    ],
  },
  {
    key: 'reputation',
    label: 'Reputation',
    icon: 'reviews',
    entries: [
      { key: 'review-sources', label: 'Review sources', path: 'review-sources' },
      { key: 'competitors', label: 'Competitors', path: 'competitors', requiresFeature: 'reports' },
      { key: 'responses', label: 'Responses', path: 'responses' },
      { key: 'widgets', label: 'Widgets', path: 'widgets', requiresFeature: 'social-sharing' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: 'confirmation_number',
    entries: [
      {
        key: 'tickets',
        label: 'Service tickets',
        path: 'tickets',
        requiresFeature: 'service-tickets',
      },
      { key: 'goals', label: 'Goals', path: 'goals' },
    ],
  },
  {
    key: 'reporting',
    label: 'Reporting',
    icon: 'summarize',
    entries: [
      {
        key: 'scheduled-reports',
        label: 'Scheduled reports',
        path: 'scheduled-reports',
        requiresFeature: 'reports',
      },
      {
        key: 'dashboard-defaults',
        label: 'Dashboard defaults',
        path: 'dashboard-defaults',
        requiresFeature: 'custom-dashboards',
      },
    ],
  },
  {
    key: 'commercial',
    label: 'Billing',
    icon: 'credit_card',
    entries: [
      { key: 'billing', label: 'Tier & billing', path: 'billing' },
      { key: 'danger-zone', label: 'Danger zone', path: 'danger-zone' },
    ],
  },
];

/**
 * The signed-in person's own settings. Nothing here changes what anyone else sees: policy lives with
 * the organisation and the property's operation with the account, and these pages only choose within
 * them.
 */
export const USER_SETTINGS_NAV: ReadonlyArray<SettingsNavGroupDefinition> = [
  {
    key: 'you',
    label: 'You',
    icon: 'person',
    entries: [
      { key: 'profile', label: 'Profile', path: 'profile', keywords: ['name', 'photo', 'avatar', 'job title', 'phone'] },
      {
        key: 'security',
        label: 'Sign-in & security',
        path: 'security',
        keywords: ['password', 'email', 'mfa', 'two-step', 'authenticator', 'sign out'],
      },
      { key: 'activity', label: 'My activity', path: 'activity', keywords: ['audit', 'history', 'log'] },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: 'notifications',
    entries: [
      {
        key: 'notifications',
        label: 'Notifications',
        path: 'notifications',
        keywords: ['alerts', 'email', 'digest', 'summary', 'daily', 'weekly', 'monthly', 'quiet hours', 'threshold'],
      },
      {
        key: 'report-emails',
        label: 'Report emails',
        path: 'report-emails',
        keywords: ['subscription', 'schedule', 'pdf', 'email me'],
      },
    ],
  },
  {
    key: 'workspace',
    label: 'Workspace',
    icon: 'tune',
    entries: [
      { key: 'appearance', label: 'Appearance', path: 'appearance', keywords: ['theme', 'dark', 'light', 'sidebar'] },
      {
        key: 'defaults',
        label: 'Defaults',
        path: 'defaults',
        keywords: ['start page', 'landing', 'scope', 'date range', 'favourites', 'quick switch'],
      },
      { key: 'lists', label: 'Lists & tables', path: 'lists', keywords: ['columns', 'density', 'rows', 'page size'] },
    ],
  },
  {
    key: 'work',
    label: 'Work',
    icon: 'work',
    entries: [
      {
        key: 'responding',
        label: 'Responding',
        path: 'responding',
        keywords: ['signature', 'sign-off', 'tone', 'reply', 'response'],
      },
      { key: 'tickets', label: 'Service tickets', path: 'tickets', keywords: ['follow', 'watch'] },
      { key: 'dashboards', label: 'Dashboards', path: 'dashboards', keywords: ['pinned', 'reset', 'layout'] },
    ],
  },
];

/** The group a section path sits in, so a deep link can light up the right tab. */
export function settingsGroupForPath(
  catalogue: ReadonlyArray<SettingsNavGroupDefinition>,
  path: string,
): SettingsNavGroupDefinition | undefined {
  return catalogue.find((group) => group.entries.some((entry) => entry.path === path));
}

export function settingsEntryForPath(
  catalogue: ReadonlyArray<SettingsNavGroupDefinition>,
  path: string,
): SettingsNavEntry | undefined {
  return catalogue.flatMap((group) => group.entries).find((entry) => entry.path === path);
}
